import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, ArrowUpDown, Eye, EyeOff, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Article {
  id: string;
  title: string;
  description: string;
  image_key: string;
  slug: string;
  is_published: boolean;
  views_count: number;
  likes_count: number;
  created_at: string;
}

export const NewsManager = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [movingArticle, setMovingArticle] = useState<Article | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image_key: "iphone-15-pro",
    slug: "",
  });

  const imageOptions = [
    { value: "iphone-15-pro", label: "iPhone 15 Pro" },
    { value: "iphone-17-pro", label: "iPhone 17 Pro" },
    { value: "macbook-air-m3", label: "MacBook Air M3" },
    { value: "mac-m5", label: "Mac M5" },
    { value: "ipad-pro-m2", label: "iPad Pro M2" },
    { value: "apple-watch-9", label: "Apple Watch 9" },
    { value: "airpods-pro", label: "AirPods Pro" },
  ];

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleAdd = async () => {
    if (!formData.title.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }

    setIsSaving(true);
    try {
      const slug = formData.slug.trim() || generateSlug(formData.title);
      
      const { error } = await supabase
        .from('articles')
        .insert({
          title: formData.title.trim(),
          description: formData.description.trim(),
          image_key: formData.image_key,
          slug,
          is_published: true,
        });

      if (error) throw error;

      toast.success("Articolo creato");
      setIsAddDialogOpen(false);
      setFormData({ title: "", description: "", image_key: "iphone-15-pro", slug: "" });
      fetchArticles();
    } catch (error) {
      console.error('Error adding article:', error);
      toast.error("Errore nella creazione");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingArticle) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('articles')
        .update({
          title: formData.title.trim(),
          description: formData.description.trim(),
          image_key: formData.image_key,
          slug: formData.slug.trim() || generateSlug(formData.title),
        })
        .eq('id', editingArticle.id);

      if (error) throw error;

      toast.success("Articolo aggiornato");
      setEditingArticle(null);
      fetchArticles();
    } catch (error) {
      console.error('Error updating article:', error);
      toast.error("Errore nell'aggiornamento");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePublish = async (article: Article) => {
    try {
      const { error } = await supabase
        .from('articles')
        .update({ is_published: !article.is_published })
        .eq('id', article.id);

      if (error) throw error;

      setArticles(prev => prev.map(a => 
        a.id === article.id ? { ...a, is_published: !a.is_published } : a
      ));
      
      toast.success(article.is_published ? "Articolo nascosto" : "Articolo pubblicato");
    } catch (error) {
      console.error('Error toggling publish:', error);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      toast.success("Articolo eliminato");
      setDeleteId(null);
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleStartMove = (article: Article) => {
    setMovingArticle(article);
    setSelectedPosition("");
  };

  const getPositionOptions = () => {
    return articles
      .filter(a => a.id !== movingArticle?.id)
      .map((a, index) => ({
        value: index.toString(),
        label: `Prima di: ${a.title.substring(0, 30)}...`,
      }));
  };

  const handleConfirmMove = () => {
    if (!movingArticle || selectedPosition === "") return;

    const newIndex = parseInt(selectedPosition);
    const currentIndex = articles.findIndex(a => a.id === movingArticle.id);
    
    const newArticles = [...articles];
    newArticles.splice(currentIndex, 1);
    newArticles.splice(newIndex, 0, movingArticle);
    
    setArticles(newArticles);
    setMovingArticle(null);
    toast.success("Ordine aggiornato");
  };

  const openEditDialog = (article: Article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      description: article.description,
      image_key: article.image_key,
      slug: article.slug,
    });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <Button onClick={() => setIsAddDialogOpen(true)} className="w-full gap-2">
          <Plus className="w-4 h-4" />
          Nuovo Articolo
        </Button>
      </div>

      {/* Articles List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {articles.map((article) => (
          <div
            key={article.id}
            className={`p-4 rounded-xl border transition-all ${
              article.is_published 
                ? 'bg-card/80 border-border/50' 
                : 'bg-muted/30 border-border/30 opacity-70'
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground truncate">{article.title}</h4>
                <p className="text-sm text-muted-foreground truncate">{article.description}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span>{article.views_count} visualizzazioni</span>
                  <span>{article.likes_count} like</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 pt-2 border-t border-border/30">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleTogglePublish(article)}
                className="gap-1"
              >
                {article.is_published ? (
                  <>
                    <EyeOff className="w-4 h-4" />
                    Nascondi
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    Pubblica
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openEditDialog(article)}
                className="gap-1"
              >
                <Pencil className="w-4 h-4" />
                Modifica
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleStartMove(article)}
                className="gap-1"
              >
                <ArrowUpDown className="w-4 h-4" />
                Sposta
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteId(article.id)}
                className="text-destructive hover:text-destructive gap-1"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Articolo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Titolo</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Titolo dell'articolo"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Descrizione</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrizione dell'articolo"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Immagine</label>
              <Select
                value={formData.image_key}
                onValueChange={(value) => setFormData({ ...formData, image_key: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {imageOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Slug (opzionale)</label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="slug-articolo"
              />
            </div>
            <Button onClick={handleAdd} className="w-full" disabled={isSaving}>
              {isSaving ? "Salvataggio..." : "Crea Articolo"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingArticle} onOpenChange={() => setEditingArticle(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Articolo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Titolo</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Descrizione</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Immagine</label>
              <Select
                value={formData.image_key}
                onValueChange={(value) => setFormData({ ...formData, image_key: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {imageOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleUpdate} className="w-full" disabled={isSaving}>
              {isSaving ? "Salvataggio..." : "Salva Modifiche"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <AlertDialog open={!!movingArticle} onOpenChange={() => setMovingArticle(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sposta Articolo</AlertDialogTitle>
            <AlertDialogDescription>
              Seleziona la nuova posizione per "{movingArticle?.title}"
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={selectedPosition} onValueChange={setSelectedPosition}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona posizione" />
              </SelectTrigger>
              <SelectContent>
                {getPositionOptions().map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmMove} disabled={!selectedPosition}>
              Conferma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'articolo?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NewsManager;
